// components/ShopPage.jsx
import React, { useState, useMemo } from 'react';
import {
  SlidersHorizontal,
  Grid3x3,
  List,
  ChevronDown,
  X,
  Star,
  Heart,
  ShoppingBag,
  ChevronRight,
} from 'lucide-react';

const ShopPage = () => {
  const [activeDesign, setActiveDesign] = useState('design1'); // 'design1' = sidebar, 'design2' = topbar
  const [viewMode, setViewMode] = useState('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSort, setSelectedSort] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  // Sample data — in real app you'd load this from API / props / context
  const products = [
    {
      id: 1,
      name: "Classic Cotton T-Shirt",
      price: 29.99,
      originalPrice: 39.99,
      category: "Clothing",
      rating: 4.5,
      reviews: 128,
      image: "bg-gradient-to-br from-blue-100 to-blue-200",
      badge: "Sale",
      sizes: ['S', 'M', 'L', 'XL'],
      colors: ['Blue', 'Black', 'White']
    },
    {
      id: 2,
      name: "Slim Fit Denim Jeans",
      price: 79.99,
      originalPrice: null,
      category: "Clothing",
      rating: 4.8,
      reviews: 89,
      image: "bg-gradient-to-br from-indigo-100 to-purple-200",
      badge: "Popular",
      sizes: ['28', '30', '32', '34'],
      colors: ['Blue', 'Black']
    },
    {
      id: 3,
      name: "Leather Crossbody Bag",
      price: 149.99,
      originalPrice: 199.99,
      category: "Accessories",
      rating: 4.7,
      reviews: 56,
      image: "bg-gradient-to-br from-amber-100 to-orange-200",
      badge: "New",
      sizes: ['One Size'],
      colors: ['Tan', 'Black', 'Brown']
    },
    {
      id: 4,
      name: "Wireless Headphones Pro",
      price: 199.99,
      originalPrice: null,
      category: "Electronics",
      rating: 4.9,
      reviews: 234,
      image: "bg-gradient-to-br from-gray-100 to-slate-200",
      badge: "Bestseller",
      sizes: ['One Size'],
      colors: ['Black', 'Silver', 'White']
    },
    {
      id: 5,
      name: "Running Sneakers Pro",
      price: 119.99,
      originalPrice: 159.99,
      category: "Footwear",
      rating: 4.6,
      reviews: 167,
      image: "bg-gradient-to-br from-green-100 to-emerald-200",
      badge: "Sale",
      sizes: ['7', '8', '9', '10', '11'],
      colors: ['Green', 'Black', 'White']
    },
    {
      id: 6,
      name: "Minimalist Watch",
      price: 299.99,
      originalPrice: null,
      category: "Accessories",
      rating: 4.8,
      reviews: 92,
      image: "bg-gradient-to-br from-rose-100 to-pink-200",
      badge: null,
      sizes: ['One Size'],
      colors: ['Silver', 'Gold', 'Black']
    },
    {
      id: 7,
      name: "Wool Blend Coat",
      price: 249.99,
      originalPrice: 349.99,
      category: "Clothing",
      rating: 4.7,
      reviews: 45,
      image: "bg-gradient-to-br from-yellow-100 to-amber-200",
      badge: "Sale",
      sizes: ['S', 'M', 'L', 'XL'],
      colors: ['Beige', 'Black', 'Navy']
    },
    {
      id: 8,
      name: "Smart Fitness Tracker",
      price: 89.99,
      originalPrice: null,
      category: "Electronics",
      rating: 4.5,
      reviews: 203,
      image: "bg-gradient-to-br from-cyan-100 to-blue-200",
      badge: "New",
      sizes: ['One Size'],
      colors: ['Black', 'Blue', 'Pink']
    }
  ];

  const categories = ['All', 'Clothing', 'Footwear', 'Accessories', 'Electronics'];
  const availableSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const availableColors = ['Black', 'White', 'Blue', 'Red', 'Green', 'Beige'];

  const toggleWishlist = (id) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const toggleSize = (size) => {
    setSelectedSizes(prev =>
      prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]
    );
  };

  const toggleColor = (color) => {
    setSelectedColors(prev =>
      prev.includes(color) ? prev.filter(c => c !== color) : [...prev, color]
    );
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedSizes([]);
    setSelectedColors([]);
    setPriceRange([0, 500]);
  };

  // Shared filtering logic
  const filteredProducts = useMemo(() => {
    let result = products.filter(p => {
      if (selectedCategory !== 'all' && p.category?.toLowerCase() !== selectedCategory) return false;
      if (selectedSizes.length > 0 && !p.sizes?.some(s => selectedSizes.includes(s))) return false;
      if (selectedColors.length > 0 && !p.colors?.some(c => selectedColors.includes(c))) return false;
      if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
      return true;
    });

    // Sorting
    if (selectedSort === 'price-low') result.sort((a, b) => a.price - b.price);
    if (selectedSort === 'price-high') result.sort((a, b) => b.price - a.price);
    if (selectedSort === 'newest') result.sort((a, b) => b.id - a.id);
    if (selectedSort === 'rating') result.sort((a, b) => b.rating - a.rating);

    return result;
  }, [products, selectedCategory, selectedSizes, selectedColors, priceRange, selectedSort]);

  // Reusable product card
  const ProductCard = ({ product, isList = false }) => (
    <div className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 ${isList ? 'flex flex-col sm:flex-row gap-6' : ''}`}>
      <div className={`relative ${isList ? 'w-full sm:w-64 aspect-square sm:aspect-auto flex-shrink-0' : 'aspect-[3/4]'}`}>
        <div className={`absolute inset-0 ${product.image} bg-cover bg-center group-hover:scale-105 transition-transform duration-500`} />
        {product.badge && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
            {product.badge}
          </span>
        )}
        <button
          onClick={() => toggleWishlist(product.id)}
          className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-gray-50"
        >
          <Heart size={22} className={wishlist.includes(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-700'} />
        </button>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 text-base">{product.name}</h3>
        <div className="flex items-center gap-1.5 mb-3">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={16}
              className={i < Math.floor(product.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
            />
          ))}
          <span className="text-sm text-gray-600 ml-1">({product.reviews})</span>
        </div>
        <div className="mt-auto flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
          {product.originalPrice && (
            <span className="text-sm text-gray-500 line-through">${product.originalPrice.toFixed(2)}</span>
          )}
        </div>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────
  // DESIGN 1: Sidebar Filters
  // ────────────────────────────────────────────────
  const Design1 = () => (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="mb-10">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Shop All</h1>
          <p className="text-gray-600">Find what you're looking for</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile filter trigger */}
          <button
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden w-full flex items-center justify-center gap-2 bg-white border border-gray-300 py-3 px-4 rounded-xl font-medium hover:bg-gray-50 mb-6"
          >
            <SlidersHorizontal size={20} /> Filters & Sort
          </button>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 xl:w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-8">
              <h2 className="text-xl font-semibold mb-6">Filters</h2>

              {/* Category */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4">Category</h3>
                <div className="space-y-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat.toLowerCase())}
                      className={`w-full text-left py-2 px-3 rounded-lg text-sm transition ${
                        selectedCategory === cat.toLowerCase() ? 'bg-gray-900 text-white font-medium' : 'hover:bg-gray-50'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4">Price</h3>
                <input
                  type="range"
                  min="0"
                  max="500"
                  value={priceRange[1]}
                  onChange={e => setPriceRange([0, Number(e.target.value)])}
                  className="w-full accent-black"
                />
                <div className="flex justify-between text-sm text-gray-700 mt-3">
                  <span>${priceRange[0]}</span>
                  <span>${priceRange[1]}</span>
                </div>
              </div>

              {/* Sizes */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4">Size</h3>
                <div className="grid grid-cols-4 gap-2">
                  {availableSizes.map(size => (
                    <button
                      key={size}
                      onClick={() => toggleSize(size)}
                      className={`py-2 border rounded-lg text-sm font-medium transition ${
                        selectedSizes.includes(size) ? 'bg-black text-white border-black' : 'border-gray-300 hover:border-gray-600'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4">Color</h3>
                <div className="flex flex-wrap gap-3">
                  {availableColors.map(color => (
                    <button
                      key={color}
                      onClick={() => toggleColor(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColors.includes(color) ? 'border-black scale-110 shadow-md' : 'border-gray-300 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.toLowerCase() }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <p className="text-gray-700">
                Showing <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products
              </p>

              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-3 rounded-xl ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                  >
                    <Grid3x3 size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-3 rounded-xl ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                  >
                    <List size={20} />
                  </button>
                </div>

                <select
                  value={selectedSort}
                  onChange={e => setSelectedSort(e.target.value)}
                  className="border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-black"
                >
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="newest">Newest</option>
                  <option value="rating">Best Rating</option>
                </select>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredProducts.map(p => <ProductCard key={p.id} product={p} isList />)}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filters Drawer */}
        {showMobileFilters && (
          <div className="fixed inset-0 bg-black/60 z-50 lg:hidden">
            <div className="absolute right-0 top-0 bottom-0 w-5/6 max-w-md bg-white overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold">Filters</h2>
                  <button onClick={() => setShowMobileFilters(false)}>
                    <X size={32} />
                  </button>
                </div>

                {/* Category */}
                <div className="mb-8">
                  <h3 className="text-sm font-semibold uppercase mb-4">Category</h3>
                  <div className="space-y-2">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat.toLowerCase())}
                        className={`w-full text-left py-3 px-4 rounded-xl text-sm ${
                          selectedCategory === cat.toLowerCase() ? 'bg-gray-900 text-white' : 'hover:bg-gray-100'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price, Sizes, Colors — copy from desktop sidebar here if needed */}

                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-full bg-black text-white py-4 rounded-xl font-semibold mt-6"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ────────────────────────────────────────────────
  // DESIGN 2: Top Filters Bar
  // ────────────────────────────────────────────────
  const Design2 = () => (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">Explore Our Collection</h1>
          <p className="text-gray-600 text-lg">Find your perfect style</p>
        </div>

        {/* Category Tabs */}
        <div className="flex overflow-x-auto gap-3 pb-4 mb-8 border-b border-gray-200">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat.toLowerCase())}
              className={`px-6 py-3 rounded-t-xl font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.toLowerCase()
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Filters Bar */}
        <div className="bg-gray-50 rounded-2xl p-6 mb-10 shadow-sm">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Sort:</span>
              <select
                value={selectedSort}
                onChange={e => setSelectedSort(e.target.value)}
                className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-black"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest</option>
                <option value="rating">Best Rating</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">Size:</span>
              {availableSizes.slice(0, 6).map(size => (
                <button
                  key={size}
                  onClick={() => toggleSize(size)}
                  className={`min-w-10 h-10 px-3 border rounded-xl text-sm font-medium transition ${
                    selectedSizes.includes(size) ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 hover:border-gray-600'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">Color:</span>
              {availableColors.slice(0, 5).map(color => (
                <button
                  key={color}
                  onClick={() => toggleColor(color)}
                  className={`px-4 py-2 border rounded-xl text-sm font-medium transition ${
                    selectedColors.includes(color) ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 hover:border-gray-600'
                  }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>

          {/* Active filters chips */}
          {(selectedSizes.length > 0 || selectedColors.length > 0) && (
            <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-gray-200">
              {selectedSizes.map(size => (
                <div
                  key={size}
                  className="bg-gray-200 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  {size}
                  <X size={16} className="cursor-pointer hover:text-red-600" onClick={() => toggleSize(size)} />
                </div>
              ))}
              {selectedColors.map(color => (
                <div
                  key={color}
                  className="bg-gray-200 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  {color}
                  <X size={16} className="cursor-pointer hover:text-red-600" onClick={() => toggleColor(color)} />
                </div>
              ))}
              <button
                onClick={clearFilters}
                className="text-red-600 text-sm font-medium hover:text-red-800 ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        <p className="text-gray-600 mb-8">
          <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products found
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {filteredProducts.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Design switcher */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveDesign('design1')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                activeDesign === 'design1'
                  ? 'bg-gray-900 text-white shadow'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Sidebar Filters
            </button>
            <button
              onClick={() => setActiveDesign('design2')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                activeDesign === 'design2'
                  ? 'bg-gray-900 text-white shadow'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Top Filters Bar
            </button>
          </div>
        </div>
      </div>

      {activeDesign === 'design1' ? <Design1 /> : <Design2 />}
    </div>
  );
};

export default ShopPage;